import {
    type CalendarDate,
    calendarToJSDate,
    jsDateToCalendar,
    todayIn,
    startOf,
    endOf,
    addDays,
} from '@/utils/dt';

export type DateRangeValue = [Date, Date] | null;
// PrimeVue represents an in-progress range as [startDate, null].
export type DateRangeDraft = Array<Date | null> | null;

export type DateRangePresetId =
    | 'today'
    | 'yesterday'
    | 'this-week'
    | 'last-week'
    | 'this-month'
    | 'last-month'
    | 'this-quarter'
    | 'last-quarter'
    | 'this-year'
    | 'last-year';

export interface DateRangePreset {
    id: DateRangePresetId
    label: string
}

export const dateRangePresets: DateRangePreset[] = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'this-week', label: 'This week' },
    { id: 'last-week', label: 'Last week' },
    { id: 'this-month', label: 'This month' },
    { id: 'last-month', label: 'Last month' },
    { id: 'this-quarter', label: 'This quarter' },
    { id: 'last-quarter', label: 'Last quarter' },
    { id: 'this-year', label: 'This year' },
    { id: 'last-year', label: 'Last year' },
];

/** Strips the time component from a Date (preserves year/month/day). */
const midnight = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export const cloneDateRangeValue = (range: DateRangeValue): DateRangeValue => {
    if (!range) {
        return null;
    }

    return [midnight(range[0]), midnight(range[1])];
};

export const toDraftRange = (range: DateRangeValue): DateRangeDraft => {
    const normalizedRange = cloneDateRangeValue(range);

    return normalizedRange ? [...normalizedRange] : null;
};

export const toCommittedRange = (range: DateRangeDraft): DateRangeValue => {
    if (!range || range.length === 0) {
        return null;
    }

    const [startDate, endDate] = range;

    if (!(startDate instanceof Date)) {
        return null;
    }

    // Single-day selection: treat the one selected date as both start and end.
    const end = endDate instanceof Date ? endDate : startDate;

    return [midnight(startDate), midnight(end)];
};

/**
 * Returns the [start, end] Date pair for the given preset, computed in the given IANA timezone.
 * Weeks start on Monday (ISO 8601). Returned Date objects are local-midnight for picker compatibility.
 */
export const getPresetRange = (
    presetId: DateRangePresetId,
    tz: string,
): [Date, Date] => {
    const today = todayIn(tz);

    let startCal: CalendarDate;
    let endCal: CalendarDate;

    switch (presetId) {
    case 'today':
        startCal = today;
        endCal = today;
        break;
    case 'yesterday':
        startCal = addDays(today, -1);
        endCal = startCal;
        break;
    case 'this-week':
        startCal = startOf('week', tz, today);
        endCal = endOf('week', tz, today);
        break;
    case 'last-week': {
        const lastWeekDay = addDays(startOf('week', tz, today), -1);
        startCal = startOf('week', tz, lastWeekDay);
        endCal = endOf('week', tz, lastWeekDay);
        break;
    }
    case 'this-month':
        startCal = startOf('month', tz, today);
        endCal = endOf('month', tz, today);
        break;
    case 'last-month': {
        const lastMonthDay = addDays(startOf('month', tz, today), -1);
        startCal = startOf('month', tz, lastMonthDay);
        endCal = endOf('month', tz, lastMonthDay);
        break;
    }
    case 'this-quarter':
        startCal = startOf('quarter', tz, today);
        endCal = endOf('quarter', tz, today);
        break;
    case 'last-quarter': {
        const lastQuarterDay = addDays(startOf('quarter', tz, today), -1);
        startCal = startOf('quarter', tz, lastQuarterDay);
        endCal = endOf('quarter', tz, lastQuarterDay);
        break;
    }
    case 'this-year':
        startCal = startOf('year', tz, today);
        endCal = endOf('year', tz, today);
        break;
    case 'last-year': {
        const lastYearDay = addDays(startOf('year', tz, today), -1);
        startCal = startOf('year', tz, lastYearDay);
        endCal = endOf('year', tz, lastYearDay);
        break;
    }
    default:
        startCal = today;
        endCal = today;
    }

    return [calendarToJSDate(startCal), calendarToJSDate(endCal)];
};

/** Returns the preset ID matching the given range in the given timezone, or null if none match. */
export const getMatchingPreset = (
    range: DateRangeValue,
    tz: string,
): DateRangePresetId | null => {
    if (!range) {
        return null;
    }

    const startCal = jsDateToCalendar(range[0]);
    const endCal = jsDateToCalendar(range[1]);

    return dateRangePresets.find(({ id }) => {
        const [ps, pe] = getPresetRange(id, tz);
        return jsDateToCalendar(ps) === startCal && jsDateToCalendar(pe) === endCal;
    })?.id ?? null;
};
